/**
 * Fonts for layout and embedding.
 *
 * The registry holds font FILES (bytes) per exact face — family + weight +
 * italic — and resolves a template {@link FontRef} plus style to one of them,
 * falling back sensibly (same family regular, then the default face) so a
 * missing variant never breaks an export. Callers hand in bytes; nothing here
 * touches the file system or network (works in the browser, a worker and jest).
 *
 * Text is measured exactly the way pdf-lib draws it: the sum of the shaped
 * glyphs' advance widths (ligatures yes, kerning no), so wrapping and
 * alignment in the PDF match the layout to the micrometer.
 */

import fontkit from '@pdf-lib/fontkit';
import { DEFAULT_CAP_HEIGHT_RATIO, type FontRef, type FontWeight } from 'shared-types';

export interface FontFace {
  /** Registry key of this exact face. */
  key: string;
  family: string;
  weight: FontWeight;
  italic: boolean;
  /** Capital height / em. */
  capHeightRatio: number;
  /** Ascender / em (positive). */
  ascentRatio: number;
  /** Descender / em (positive, below the baseline). */
  descentRatio: number;
  /** Advance width of `text` in em (multiply by the em size). */
  measure(text: string): number;
}

/** Measured texts kept per face (cleared when full: a long session can't grow it without bound). */
const MEASURE_CACHE_SIZE = 20_000;

export interface FontResolver {
  resolve(font: FontRef, weight: FontWeight, italic: boolean): FontFace;
}

interface RegisteredFace extends FontFace {
  source: 'bundled' | 'org';
  /** Org font id (brand kit), for `{ source: 'org', assetId }` references. */
  orgId: string | null;
  bytes: Uint8Array;
}

export interface FontFileInfo {
  family: string;
  weight: FontWeight;
  italic: boolean;
}

function faceKey(source: 'bundled' | 'org', family: string, weight: FontWeight, italic: boolean): string {
  return `${source}:${family}:${weight}:${italic ? 'italic' : 'normal'}`;
}

export class FontRegistry implements FontResolver {
  private readonly faces = new Map<string, RegisteredFace>();
  private readonly orgFaces = new Map<string, RegisteredFace>();
  private defaultKey: string | null = null;

  /** Registers a shipped font file. The first regular face registered becomes the fallback of last resort. */
  addBundled(info: FontFileInfo, bytes: Uint8Array): FontFace {
    const face = this.createFace('bundled', null, info, bytes);
    if (this.defaultKey === null && info.weight === 'normal' && !info.italic) this.defaultKey = face.key;
    return face;
  }

  /** Registers an organisation font (brand kit) under its id. */
  addOrg(orgId: string, info: FontFileInfo, bytes: Uint8Array): FontFace {
    const face = this.createFace('org', orgId, info, bytes);
    this.orgFaces.set(orgId, face);
    return face;
  }

  setDefault(key: string): void {
    if (!this.faces.has(key)) throw new Error(`Unknown font face ${key}`);
    this.defaultKey = key;
  }

  /** Font file bytes of a face key (for embedding). */
  bytesFor(key: string): Uint8Array | null {
    return this.faces.get(key)?.bytes ?? null;
  }

  faceFor(key: string): FontFace | null {
    return this.faces.get(key) ?? null;
  }

  keys(): string[] {
    return [...this.faces.keys()];
  }

  resolve(font: FontRef, weight: FontWeight, italic: boolean): FontFace {
    let source: 'bundled' | 'org' = 'bundled';
    let family: string | null = null;
    if (font.source === 'org') {
      const org = this.orgFaces.get(font.assetId);
      if (org) {
        source = 'org';
        family = org.family;
      }
    } else {
      family = font.family;
    }
    if (family !== null) {
      // Exact face, then drop italic, then drop bold, then the family's regular.
      const candidates: [FontWeight, boolean][] = [
        [weight, italic],
        [weight, false],
        ['normal', italic],
        ['normal', false],
      ];
      for (const [w, i] of candidates) {
        const face = this.faces.get(faceKey(source, family, w, i));
        if (face) return face;
      }
    }
    const fallback = this.defaultKey ? this.faces.get(this.defaultKey) : undefined;
    if (!fallback) throw new Error('FontRegistry has no fonts registered');
    return fallback;
  }

  private createFace(source: 'bundled' | 'org', orgId: string | null, info: FontFileInfo, bytes: Uint8Array): RegisteredFace {
    const font = fontkit.create(bytes);
    const em = font.unitsPerEm;
    const capHeight = font.capHeight > 0 ? font.capHeight / em : DEFAULT_CAP_HEIGHT_RATIO;
    const key = faceKey(source, info.family, info.weight, info.italic);
    // Shaping is the costly part of laying text out (wrapping and auto-shrink measure the same words again and
    // again, every redraw): widths are kept per text, in em, so one shaping serves every size.
    const widths = new Map<string, number>();
    const measure = (text: string): number => {
      if (!text) return 0;
      const known = widths.get(text);
      if (known !== undefined) return known;
      const width = font.layout(text).glyphs.reduce((sum, glyph) => sum + glyph.advanceWidth, 0) / em;
      if (widths.size >= MEASURE_CACHE_SIZE) widths.clear();
      widths.set(text, width);
      return width;
    };
    const face: RegisteredFace = {
      key,
      family: info.family,
      weight: info.weight,
      italic: info.italic,
      capHeightRatio: capHeight,
      ascentRatio: font.ascent / em,
      descentRatio: Math.abs(font.descent) / em,
      measure,
      source,
      orgId,
      bytes,
    };
    this.faces.set(key, face);
    return face;
  }
}

/** What a font file says about itself: family name and style (null when it can't be read as a font). */
export interface FontFileDescription {
  family: string;
  bold: boolean;
  italic: boolean;
}

/**
 * Reads a TTF / OTF file's family and style from its own tables, so an
 * upload needs no typing: the style name decides bold (bold, semibold,
 * black, heavy) and italic (italic, oblique, or a slanted design).
 */
export function describeFontFile(bytes: Uint8Array): FontFileDescription | null {
  try {
    const font = fontkit.create(bytes);
    const family = (font.familyName ?? font.fullName ?? '').trim();
    if (!family) return null;
    const style = font.subfamilyName ?? '';
    return { family, bold: /bold|black|heavy/i.test(style), italic: /italic|oblique/i.test(style) || Math.abs(font.italicAngle ?? 0) > 0.5 };
  } catch {
    return null;
  }
}
